import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  BadgeIndianRupee,
  GraduationCap,
  Building2,
  CheckCircle2,
  Calendar,
  Users,
  Award,
  ArrowRight,
} from "lucide-react";
import PageHero from "../components/PageHero";
import { EditableText, EditableImage } from "../components/live/Editable";
import api from "../lib/api";

const STEPS = [
  {
    icon: GraduationCap,
    title: "Year 1 — Foundation at the Institute",
    text: "Study all the essential General Engineering based subjects such as Engineering Drawing, Metrology, Workshop Technology & Calculations, Electrical, Electronics, Material Science, Mechanical Workshop, Welding, Electrical Practical etc. — learning a vast variety of skills essential to succeed when entering industry.",
  },
  {
    icon: Calendar,
    title: "Year 2 — Work-Study Begins: 4 Days Company + 2 Days Institute",
    text: "Start the Work-Study part of the course, receiving Internship from leading companies — MNC, Indian, German, American and Japanese companies. Spend 4 days per week at companies learning in the industry and 2 days per week at the institute learning higher level skills and knowledge.",
  },
  {
    icon: Building2,
    title: "Year 2 & 3 — Earn a Stipend / Training Allowance",
    text: "Students receive a stipend / training allowance from companies in the 2nd and 3rd year of their study while continuing the 4-day company, 2-day institute training rhythm.",
  },
  {
    icon: Award,
    title: "Final 6 Months — Design, Exam Projects & Final Placement",
    text: "Return to the institute full time to learn Design and other Higher Level skills and complete Exam Projects. Use this time to build your study and internship profiles and get Final Placement Offers from various companies in India and abroad.",
  },
];

const CRITERIA = [
  "18 years of age or above",
  "Maintain at least 85% attendance",
  "Score a minimum of 50% marks",
  "Obtain a pass in soft skills",
];

const BENEFITS = [
  { icon: BadgeIndianRupee, title: "Earn a Stipend", text: "Receive a training allowance from partner companies from your 2nd year onwards." },
  { icon: Users, title: "Real Industry Experience", text: "Learn theoretically and practically side by side, just like the German apprenticeship system." },
  { icon: Award, title: "German Certification", text: "Certified by the German Chamber of Skilled Crafts (HWK), Koblenz, Germany." },
  { icon: Building2, title: "Entrepreneurial Edge", text: "Learn marketing, accounts and compliance — ideal for running a family business or starting your own." },
];

export default function EarnWhileYouLearnPage() {
  const [courses, setCourses] = useState([]);
  useEffect(() => {
    api
      .get("/courses", { params: { active_only: true, category: "long_term" } })
      .then((r) => setCourses(r.data))
      .catch(() => setCourses([]));
  }, []);

  return (
    <>
      <PageHero
        path="ewyl.hero"
        kicker="Work-Study Model"
        title="Earn While You Learn"
        subtitle="Based on the German Berufsschule model, students train at the institute and at a partner company — receiving a stipend from their 2nd year onwards."
      />

      {/* Intro */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-10 items-center">
          <EditableImage
            path="ewyl.main.image"
            src="/assets/kiki/ewyl-main.jpg"
            alt="Earn While You Learn at Kiki Centre"
            className="rounded-3xl shadow-xl overflow-hidden"
            imgClassName="w-full h-full object-cover"
            data-testid="ewyl-main-image"
          />
          <div>
            <span className="inline-block px-3 py-1 rounded-full bg-yellow-400/15 text-yellow-700 text-xs font-bold tracking-wider uppercase mb-4">
              The German Berufsschule System
            </span>
            <EditableText path="ewyl.intro.title" as="h2" className="text-2xl sm:text-3xl font-bold text-[#0B2A6B] mb-4 block">
              Learn at the institute. Train at a company. Get paid while you do it.
            </EditableText>
            <EditableText path="ewyl.intro.text" as="p" multiline className="text-slate-600 leading-relaxed mb-4 block" data-testid="ewyl-intro-text">
              Students at Kiki study in the International Dual Model of Education or Work-Study Program as prevalent in Germany, Switzerland etc. Students learn simultaneously at the institute and at a company — creating a highly skilled workforce with both theoretical and practical knowledge, and giving students the possibility to Earn While They Learn.
            </EditableText>
            <div className="grid sm:grid-cols-2 gap-4 mt-6">
              <div className="rounded-2xl bg-yellow-50 border border-yellow-100 p-5" data-testid="ewyl-stipend-stat">
                <EditableText path="ewyl.stipend.label" as="p" className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1 block">Estimated Stipend during Internship</EditableText>
                <p className="text-xl font-bold text-[#0B2A6B]"><EditableText path="ewyl.stipend.value" as="span">₹10,000 – ₹15,000</EditableText> <span className="text-sm font-semibold text-slate-500">monthly</span></p>
              </div>
              <div className="rounded-2xl bg-yellow-50 border border-yellow-100 p-5" data-testid="ewyl-salary-stat">
                <EditableText path="ewyl.salary.label" as="p" className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1 block">Estimated Salaries after Final Placement</EditableText>
                <p className="text-xl font-bold text-[#0B2A6B]"><EditableText path="ewyl.salary.value" as="span">₹2,40,000 – ₹5,60,000</EditableText> <span className="text-sm font-semibold text-slate-500">annually</span></p>
              </div>
            </div>
            <p className="text-slate-600 mt-5 text-sm">
              For full details contact our Academic Counsellors on{" "}
              <a href="tel:+918800288994" className="font-bold text-[#0B2A6B] hover:text-yellow-600">88002 88994</a>.
            </p>
            <div className="flex flex-wrap gap-3 mt-5">
              <a
                href="https://kikicentre.com/wp-content/uploads/2026/04/Students-Internship-2025.pdf"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-slate-200 bg-white text-sm font-semibold text-[#0B2A6B] hover:border-yellow-400 transition"
                data-testid="ewyl-internship-pdf"
              >
                Download the Student Internship
              </a>
              <a
                href="https://kikicentre.com/wp-content/uploads/2026/04/Info-Sheet-Earn-While-You-Learn.pdf"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-slate-200 bg-white text-sm font-semibold text-[#0B2A6B] hover:border-yellow-400 transition"
                data-testid="ewyl-infosheet-pdf"
              >
                Info Sheet: Earn While You Learn
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {BENEFITS.map((b, i) => {
              const Icon = b.icon;
              return (
                <div
                  key={b.title}
                  className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm hover:shadow-xl tilt-card animate-fade-up"
                  style={{ animationDelay: `${i * 0.06}s` }}
                  data-testid={`ewyl-benefit-${i}`}
                >
                  <div className="h-12 w-12 rounded-xl bg-yellow-400 flex items-center justify-center mb-4">
                    <Icon className="h-6 w-6 text-[#0B2A6B]" />
                  </div>
                  <h3 className="font-bold text-[#0B2A6B] mb-2">{b.title}</h3>
                  <p className="text-sm text-slate-600">{b.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Journey timeline */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#0B2A6B]">Your 3½ Year Journey</h2>
            <p className="text-slate-600 mt-2">From classroom foundation to a paid, certified technician.</p>
          </div>
          <div className="space-y-5">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              return (
                <div
                  key={s.title}
                  className="flex items-start gap-5 bg-white rounded-2xl p-6 border border-slate-100 shadow-sm animate-fade-up"
                  style={{ animationDelay: `${i * 0.08}s` }}
                >
                  <div className="shrink-0 h-12 w-12 rounded-xl bg-[#0B2A6B] text-white flex items-center justify-center font-bold">
                    <Icon className="h-6 w-6 text-yellow-400" />
                  </div>
                  <div>
                    <h3 className="font-bold text-[#0B2A6B] text-lg mb-1"><EditableText path={`ewyl.step.${i}.title`} as="span">{s.title}</EditableText></h3>
                    <p className="text-slate-600 text-sm"><EditableText path={`ewyl.step.${i}.text`} as="span" multiline>{s.text}</EditableText></p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Eligibility for stipend */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold text-[#0B2A6B] mb-4">
              Do I earn while I learn?
            </h2>
            <p className="text-slate-600 mb-6">
              Students who meet the following criteria have the opportunity, in their 2nd year, to apply to
              partner companies for the work-study model. While training at the company, students receive a
              training allowance.
            </p>
            <ul className="space-y-3">
              {CRITERIA.map((c) => (
                <li key={c} className="flex items-start gap-3 p-4 rounded-xl bg-yellow-50 border border-yellow-100">
                  <CheckCircle2 className="h-5 w-5 text-yellow-600 mt-0.5 shrink-0" />
                  <span className="font-medium text-slate-700">{c}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-2xl bg-[#0B2A6B] text-white p-8 shadow-lg">
            <BadgeIndianRupee className="h-10 w-10 text-yellow-400 mb-4" />
            <h3 className="text-xl font-bold mb-3">Long Term Courses Eligible for Earn While You Learn</h3>
            <div className="space-y-2">
              {courses.length === 0 && (
                <p className="text-slate-300 text-sm">Loading eligible courses…</p>
              )}
              {courses.map((c) => (
                <Link
                  key={c.id}
                  to={`/courses/detail/${c.slug}`}
                  className="flex items-center justify-between gap-3 rounded-lg bg-white/10 hover:bg-white/20 transition px-4 py-3"
                  data-testid={`ewyl-course-${c.slug}`}
                >
                  <span className="font-semibold text-sm">{c.title}</span>
                  <ArrowRight className="h-4 w-4 text-yellow-400 shrink-0" />
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="pb-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-3xl bg-yellow-400 p-10 text-center">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#0B2A6B] mb-3">
              Start earning while you build your career
            </h2>
            <p className="text-[#0B2A6B]/80 mb-6 max-w-2xl mx-auto">
              Apply for admission to a 3½-year German Technical Training Course and step into the work-study model
              from your 2nd year.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <Link
                to="/admissions"
                className="px-7 py-3 rounded-lg bg-[#0B2A6B] text-white font-semibold hover:bg-[#08205a] transition inline-flex items-center gap-2"
                data-testid="ewyl-apply-btn"
              >
                Apply Now <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/courses/long-term"
                className="px-7 py-3 rounded-lg border-2 border-[#0B2A6B] text-[#0B2A6B] font-semibold hover:bg-[#0B2A6B] hover:text-white transition"
                data-testid="ewyl-courses-btn"
              >
                View Long Term Courses
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
